/**
 * @fileoverview Input/output schemas for the draft-all-chapters composite agent.
 *
 * This composite agent sequentially drafts all planned chapters using either
 * chapter-drafter-v2 or chapter-drafter-enhanced-v2 for each chapter.
 *
 * Note: The actual flow execution happens client-side in the hook (not via a
 * Genkit server flow) because it needs to refresh project state between chapters.
 * These schemas define the hook-level contract.
 */

import { z } from 'zod';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Configuration provided by the user via the dialog.
 * Applied identically to every chapter.
 */
export const HookInputSchema = z.object({
  /** Which action to use for each chapter */
  actionType: z.enum(['chapter-drafter-v2', 'chapter-drafter-enhanced-v2', 'draft-refiner-v2']),
  /** User instructions applied to every chapter */
  userMessage: z.string(),
  /** Style guide content (optional) */
  styleGuideId: z.string().optional(),
  /** Writing style sample (optional) */
  writingStyleSample: z.string().optional(),
  /** Model for standard draft (chapter-drafter-v2) or draft step (enhanced) */
  draftModel: z.string().optional(),
  /** Model for refine step (enhanced drafter only) */
  refineModel: z.string().optional(),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// PER-CHAPTER RESULT SCHEMA
// =============================================================================

export const ChapterResultSchema = z.object({
  chapterId: z.string(),
  chapterTitle: z.string(),
  success: z.boolean(),
  commentary: z.string().optional(),
  error: z.string().optional(),
  durationMs: z.number().optional(),
});

export type ChapterResult = z.infer<typeof ChapterResultSchema>;

// =============================================================================
// PROGRESS STATE SCHEMA
// =============================================================================

export const ProgressStateSchema = z.object({
  phase: z.enum(['idle', 'running', 'cancelled', 'complete', 'error']),
  currentChapterIndex: z.number(),
  totalChapters: z.number(),
  currentChapterTitle: z.string().optional(),
  completedChapters: z.array(ChapterResultSchema),
});

export type ProgressState = z.infer<typeof ProgressStateSchema>;

// =============================================================================
// OUTPUT SCHEMA (for agent definition compliance)
// =============================================================================

export const OutputSchema = z.object({
  completedChapters: z.array(ChapterResultSchema),
  totalDurationMs: z.number(),
});

export type Output = z.infer<typeof OutputSchema>;
